import { useEffect, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';

export interface SubmissionWindow {
  /** When the tenant stops accepting ideas, votes and comments; null when it never does. */
  deadline: Date | null;
  msRemaining: number;
  isClosed: boolean;
}

const ALWAYS_OPEN: SubmissionWindow = { deadline: null, msRemaining: Infinity, isClosed: false };
// setTimeout silently fires immediately above this delay
const MAX_TIMER_DELAY = 2 ** 31 - 1;

export const getSubmissionWindow = (
  closeAt?: string | null,
  now: number = Date.now()
): SubmissionWindow => {
  if (!closeAt) return ALWAYS_OPEN;
  const deadline = new Date(closeAt);
  if (Number.isNaN(deadline.getTime())) return ALWAYS_OPEN;
  const msRemaining = Math.max(0, deadline.getTime() - now);
  return { deadline, msRemaining, isClosed: msRemaining === 0 };
};

/**
 * The current tenant's submission window. Re-renders once when the deadline passes,
 * or every second while open when `tick` is set (for a countdown).
 */
export const useSubmissionWindow = ({ tick = false }: { tick?: boolean } = {}): SubmissionWindow => {
  const { tenantConfig } = useTenant();
  const closeAt = tenantConfig?.submissions_close_at;
  const [now, setNow] = useState(() => Date.now());
  const window = getSubmissionWindow(closeAt, now);
  const { deadline, isClosed } = window;
  const deadlineMs = deadline?.getTime();

  // Countdown mode: tick every second until closed
  useEffect(() => {
    if (!tick || deadlineMs === undefined || isClosed) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tick, deadlineMs, isClosed]);

  // Flip mode: wake up at the deadline (re-armed after each wake for far-off deadlines)
  useEffect(() => {
    if (tick || deadlineMs === undefined || isClosed) return undefined;
    const delay = Math.min(Math.max(0, deadlineMs - Date.now()), MAX_TIMER_DELAY);
    const id = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(id);
  }, [tick, deadlineMs, isClosed, now]);

  return window;
};
